import { CalendarDays, CheckCircle2, History, Loader2, Lock, NotebookPen, Timer, Users } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router'
import { toast } from 'sonner'
import { PageHeader } from '@/components/common/PageHeader'
import { EmptyState } from '@/components/common/States'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { useAuth } from '@/lib/auth/AuthProvider'
import { fmtDate } from '../shared/format'
import { useCustomer, useFinishMeeting, useMeetings } from './api'
import { ChannelLabel } from './components/CrmBits'
import { MeetingMinutes, MinuteRow } from './components/detail/MeetingMinutes'
import { LiveSidebar } from './components/live/LiveSidebar'
import { CRM_MEETING_WRITE_PERM } from './permissions'
import type { Meeting } from './types'
import { toastError, useCrm } from './ui'

/** Bugün başlayıp henüz bitmemiş toplantının geçen süresi (dakika); dakikada bir güncellenir. */
function useElapsed(m: Meeting | undefined) {
  const [now, setNow] = useState(() => Date.now())
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 30_000)
    return () => window.clearInterval(id)
  }, [])
  if (!m || m.end || m.date !== new Date(now).toISOString().slice(0, 10)) return null
  const started = new Date(`${m.date}T${m.start}:00`).getTime()
  return Math.max(0, Math.floor((now - started) / 60_000))
}

/** Toplantı modu: müşterinin yanındayken hızlı not almak için tek odaklı sayfa. */
export function MeetingLivePage() {
  const { t } = useTranslation()
  const { id = '', meetingId = '' } = useParams()
  const navigate = useNavigate()
  const { can } = useAuth()
  const { staffName } = useCrm()
  const customer = useCustomer(id)
  const meetings = useMeetings()
  const finish = useFinishMeeting()
  const canWrite = can(CRM_MEETING_WRITE_PERM)
  const m = meetings.data?.find((x) => x.id === meetingId && x.customerId === id)
  const elapsed = useElapsed(m)
  const backTo = `/customers/${id}/meetings?m=${meetingId}`

  if (!customer.data || !meetings.data) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-20 rounded-xl" />
        <Skeleton className="h-96 rounded-xl" />
      </div>
    )
  }
  if (!m) return <EmptyState icon={NotebookPen} title={t('crm.live.notFound')} />
  if (m.restricted) return <EmptyState icon={Lock} title={t(m.visibility === 'private' ? 'crm.meeting.privateLocked' : 'crm.meeting.restrictedLocked')} />

  const c = customer.data.customer
  const previous = meetings.data.filter((x) => x.customerId === id && x.id !== m.id && `${x.date}${x.start}` < `${m.date}${m.start}`)[0]
  const carryOver = previous?.minutes.filter((i) => !i.done) ?? []

  const onFinish = async () => {
    try {
      await finish.mutateAsync(m.id)
      toast.success(t('crm.live.finished'))
      navigate(backTo)
    } catch (e) {
      toastError(e)
    }
  }

  return (
    <>
      <PageHeader
        backTo={backTo}
        eyebrow={t('crm.live.eyebrow')}
        title={c.name}
        description={
          <span className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm">
            <span className="inline-flex items-center gap-1.5">
              <CalendarDays className="size-4" aria-hidden />
              {fmtDate(m.date)} · {m.start}
              {m.end ? ` – ${m.end}` : ''}
            </span>
            <ChannelLabel channel={m.channel} />
            <span className="inline-flex items-center gap-1.5">
              <Users className="size-4" aria-hidden />
              {staffName(m.staffId)}
            </span>
          </span>
        }
        actions={
          <>
            {elapsed !== null ? (
              <span className="inline-flex h-9 items-center gap-1.5 rounded-lg bg-success/12 px-3 text-sm font-medium tabular-nums text-success">
                <span className="size-2 animate-pulse rounded-full bg-success" aria-hidden />
                <Timer className="size-4" aria-hidden />
                {t('crm.live.elapsed', { min: elapsed })}
              </span>
            ) : null}
            {canWrite && !m.end ? (
              <Button onClick={() => void onFinish()} disabled={finish.isPending}>
                {finish.isPending ? <Loader2 className="animate-spin" /> : <CheckCircle2 />}
                {t('crm.live.finish')}
              </Button>
            ) : null}
          </>
        }
      />

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[minmax(0,1fr)_20rem] xl:grid-cols-[minmax(0,1fr)_22rem]">
        <div className="min-w-0 space-y-4">
          <section className="rounded-xl border bg-card p-4 shadow-card sm:p-6">
            <MeetingMinutes meeting={m} canWrite={canWrite} large />
          </section>

          {carryOver.length && previous ? (
            <section className="rounded-xl border bg-card p-4 shadow-card">
              <h2 className="flex items-center gap-2 text-sm font-semibold">
                <History className="size-4 text-warning" aria-hidden />
                {t('crm.live.carryOver')}
              </h2>
              <p className="mb-2 text-xs text-muted-foreground">{t('crm.live.carryOverHint', { date: fmtDate(previous.date) })}</p>
              <ul className="-mx-2">
                {carryOver.map((i) => (
                  <MinuteRow key={i.id} meetingId={previous.id} item={i} canWrite={canWrite} />
                ))}
              </ul>
            </section>
          ) : null}
        </div>

        <LiveSidebar customer={c} />
      </div>
    </>
  )
}
